import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  ArrowLeft,
  ChartBar,
  CurrencyDollar,
  Path,
  Percent,
  Truck,
  TrendUp,
  Trophy,
} from "@phosphor-icons/react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import api from "@/lib/api";
import { fmtMoney, fmtRPM } from "@/lib/defaults";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

const Kpi = ({ label, value, hint, icon, tone = "default", testId }) => {
  const tc =
    tone === "positive"
      ? "text-emerald-300"
      : tone === "negative"
        ? "text-red-300"
        : "text-white";
  return (
    <div
      data-testid={testId}
      className="glass rounded-2xl p-5 md:p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-medium">
            {label}
          </p>
          <p className={`tabular text-3xl font-semibold mt-2 leading-none ${tc}`}>
            {value}
          </p>
          {hint && (
            <p className="mt-2 text-[11px] text-slate-500 tabular">{hint}</p>
          )}
        </div>
        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/25 grid place-items-center text-emerald-300">
          {icon}
        </div>
      </div>
    </div>
  );
};

export default function Analytics() {
  const { isAuthenticated, loading } = useAuth();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [fetching, setFetching] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      nav("/login", { state: { from: "/app/analytics" } });
      return;
    }
    (async () => {
      setFetching(true);
      try {
        const res = await api.get("/analytics/weekly", { params: { days } });
        setData(res.data);
      } catch {
        toast.error("Failed to load analytics");
      } finally {
        setFetching(false);
      }
    })();
  }, [days, isAuthenticated, loading, nav]);

  const daily = data?.daily || [];
  const summary = data?.summary || {};
  const top = data?.top_loads || [];

  const chartConfig = useMemo(
    () => ({
      grid: "#1f2a44",
      axis: "#64748b",
      line: "#10b981",
      bar: "#3b82f6",
    }),
    []
  );

  return (
    <div className="min-h-screen bg-[#0a192f] text-white landing-root">
      <div className="ticker-line" />

      <header className="max-w-[1240px] mx-auto px-5 md:px-8 py-5 flex items-center justify-between">
        <Link to="/app" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
          <ArrowLeft size={16} weight="bold" className="flip-x" />
          Back to calculator
        </Link>

        <div className="flex items-center gap-2">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              data-testid={`btn-range-${d}`}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-xs uppercase tracking-[0.14em] font-medium transition-colors ${
                days === d
                  ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300"
                  : "border border-white/10 text-slate-300 hover:border-white/25"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-[1240px] mx-auto px-5 md:px-8 pb-16">
        <div className="mb-8">
          <div className="kicker">
            <span className="dot" />
            Analytics
          </div>
          <h1 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
            Last {days} days at a glance.
          </h1>
          <p className="mt-2 text-slate-400 text-sm md:text-base">
            Which routes actually paid your driver — measured, not guessed.
          </p>
        </div>

        {fetching && !data ? (
          <div className="h-32 rounded-2xl glass grid place-items-center text-slate-400">
            Loading analytics…
          </div>
        ) : summary.count === 0 ? (
          <div
            data-testid="analytics-empty"
            className="glass rounded-2xl p-10 text-center"
          >
            <ChartBar size={28} weight="duotone" className="text-emerald-400 mx-auto" />
            <p className="mt-3 text-white font-semibold">
              No loads screened in this window yet.
            </p>
            <p className="mt-1 text-sm text-slate-400">
              Save your first load on the calculator to unlock analytics.
            </p>
            <Link
              to="/app"
              className="btn-neon inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-xl text-sm"
            >
              Go to calculator
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              <Kpi
                testId="stat-count"
                label="Loads screened"
                value={summary.count}
                icon={<Truck size={18} weight="duotone" />}
              />
              <Kpi
                testId="stat-revenue"
                label="Total revenue"
                value={fmtMoney(summary.total_revenue)}
                icon={<CurrencyDollar size={18} weight="duotone" />}
              />
              <Kpi
                testId="stat-net"
                label="Net profit"
                value={fmtMoney(summary.total_net_profit)}
                icon={<TrendUp size={18} weight="duotone" />}
                tone={summary.total_net_profit >= 0 ? "positive" : "negative"}
              />
              <Kpi
                testId="stat-rpm"
                label="Avg RPM"
                value={fmtRPM(summary.avg_rpm)}
                icon={<ChartBar size={18} weight="duotone" />}
              />
              <Kpi
                testId="stat-ppm"
                label="Avg profit/mile"
                value={fmtRPM(summary.avg_profit_per_mile)}
                icon={<Path size={18} weight="duotone" />}
                tone={summary.avg_profit_per_mile >= 0 ? "positive" : "negative"}
              />
              <Kpi
                testId="stat-deadhead"
                label="Deadhead ratio"
                value={`${(summary.deadhead_ratio * 100).toFixed(1)}%`}
                icon={<Percent size={18} weight="duotone" />}
                hint={`${summary.profitable_pct}% profitable`}
              />
            </div>

            {/* Charts */}
            <div className="mt-6 grid lg:grid-cols-2 gap-4">
              <div className="glass rounded-2xl p-5 md:p-6">
                <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-medium">
                  Daily net profit
                </p>
                <h3 className="text-white text-xl font-semibold tracking-tight mt-1">
                  Profit trend
                </h3>
                <div className="h-64 mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={daily} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
                      <CartesianGrid stroke={chartConfig.grid} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="day"
                        tick={{ fill: chartConfig.axis, fontSize: 11 }}
                        stroke={chartConfig.grid}
                      />
                      <YAxis
                        tick={{ fill: chartConfig.axis, fontSize: 11 }}
                        stroke={chartConfig.grid}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#0e2140",
                          border: "1px solid rgba(255,255,255,0.15)",
                          borderRadius: 8,
                          color: "#e2e8f0",
                        }}
                        formatter={(v) => [`$${Number(v).toFixed(2)}`, "Net"]}
                      />
                      <Line
                        type="monotone"
                        dataKey="net_profit"
                        stroke={chartConfig.line}
                        strokeWidth={2}
                        dot={{ r: 3, fill: chartConfig.line }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="glass rounded-2xl p-5 md:p-6">
                <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-medium">
                  Daily revenue
                </p>
                <h3 className="text-white text-xl font-semibold tracking-tight mt-1">
                  Revenue by day
                </h3>
                <div className="h-64 mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={daily} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
                      <CartesianGrid stroke={chartConfig.grid} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="day"
                        tick={{ fill: chartConfig.axis, fontSize: 11 }}
                        stroke={chartConfig.grid}
                      />
                      <YAxis
                        tick={{ fill: chartConfig.axis, fontSize: 11 }}
                        stroke={chartConfig.grid}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#0e2140",
                          border: "1px solid rgba(255,255,255,0.15)",
                          borderRadius: 8,
                          color: "#e2e8f0",
                        }}
                        formatter={(v) => [`$${Number(v).toFixed(2)}`, "Revenue"]}
                      />
                      <Bar dataKey="revenue" fill={chartConfig.bar} radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Top loads */}
            <div className="mt-6 glass rounded-2xl overflow-hidden">
              <div className="p-5 md:p-6 border-b border-white/8 flex items-center gap-3">
                <Trophy size={20} weight="duotone" className="text-amber-300" />
                <h3 className="text-white text-xl font-semibold tracking-tight">
                  Top 5 most profitable loads
                </h3>
              </div>

              {top.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">
                  No loads to rank yet.
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="top-loads-table">
                  <thead className="bg-white/[0.02]">
                    <tr className="text-[10px] uppercase tracking-[0.12em] text-slate-400">
                      <th className="text-start p-4">#</th>
                      <th className="text-start p-4">Route</th>
                      <th className="text-end p-4">Rate</th>
                      <th className="text-end p-4">Net</th>
                      <th className="text-end p-4">$/mile</th>
                      <th className="p-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top.map((l, i) => (
                      <tr
                        key={l.id}
                        className="border-t border-white/6 hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="p-4 tabular text-slate-400">#{i + 1}</td>
                        <td className="p-4">
                          <p className="text-white font-medium">
                            {l.pick_city} → {l.delivery_city}
                          </p>
                          <p className="text-slate-500 text-xs">
                            {Math.round(l.total_miles).toLocaleString()} miles
                          </p>
                        </td>
                        <td className="p-4 tabular text-end text-slate-200">
                          {fmtMoney(l.rate)}
                        </td>
                        <td className={`p-4 tabular text-end font-semibold ${l.net_profit >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                          {fmtMoney(l.net_profit)}
                        </td>
                        <td className="p-4 tabular text-end text-slate-200">
                          {fmtRPM(l.profit_per_mile)}
                        </td>
                        <td className="p-4">
                          {l.is_profitable ? (
                            <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded-md text-[10px] uppercase tracking-[0.14em] hover:bg-emerald-500/15">
                              Profitable
                            </Badge>
                          ) : (
                            <Badge className="bg-red-500/15 text-red-300 border border-red-500/30 rounded-md text-[10px] uppercase tracking-[0.14em] hover:bg-red-500/15">
                              Not profitable
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </main>
      <Toaster position="top-right" richColors closeButton theme="dark" />
    </div>
  );
}
